import React from 'react';

import EnhancedModal from '../../../common/EnhancedModal/EnhancedModal';
import ImagePreview from '../ProductsPreviewSection/ImagePreview';
import { TruncatedText } from '../../../common';
import { formatCurrency } from '../../../../utils/global';
import EditIcon from '../../../../assets/icons/edit-icon';
import TrashIcon from '../../../../assets/icons/trash-icon';
import styles from './confirmProducts.module.css';
import ModalCommonButtonsContainer from '../../../common/ModalComponent/ModalCommonButtonsContainer';
import CustomTooltip from '../../../common/CustomTooltip/CustomTooltip';
import useIsMobile from '../../../../hooks/useIsMobile';
import CartIcon from '../../../../assets/icons/CartIcon';
import DollarIcon from '../../../../assets/icons/DollarIcon';

interface Props {
  open: boolean;
  onClose: () => void;
  orderDetails: any[];
  defaultCurrency: string;
  removeProduct: (productId: string) => void;
  onSave: () => void;
  handleBack: () => void;
}

export default function ConfirmProducts({
  open,
  onClose,
  orderDetails,
  defaultCurrency,
  removeProduct,
  onSave,
  handleBack,
}: Props) {
  const isMobile = useIsMobile();

  const totalItems = orderDetails.reduce((acc, product) => acc + (product?.quantity || 1), 0);
  const totalValue = orderDetails.reduce(
    (acc, product) => acc + (product?.quantity || 1) * (product?.productDetails?.productPrice || 0),
    0
  );
  return (
    <div>
      <EnhancedModal
        open={open}
        onClose={onClose}
        title="Selected Products"
        showBackButton
        handleBack={handleBack}
        showCloseButton
        width={396}
        bodyClassName={`${isMobile ? 'px-4' : 'px-6'}`}
        footerClassName="p-0"
        footer={
          <ModalCommonButtonsContainer
            submitButtonText={<span className={isMobile ? 'text-sm' : 'text-base'}>Confirm Address</span>}
            cancelButtonText={<span className={isMobile ? 'text-sm' : 'text-base'}>Go Back</span>}
            onClose={handleBack}
            onSave={onSave}
            className={`${isMobile ? 'p-4 flex flex-col-reverse' : 'p-6'}`}
          />
        }
      >
        {orderDetails.map((product, i) => {
          const productDetails = product?.productDetails;
          const variantOptions = productDetails?.variantDetail;
          const options = Object.keys(variantOptions)?.map((option: any) => ({
            name: option,
            value: variantOptions[option],
          }));
          return (
            <div className={`${styles.singleProductPreview} border-default rounded`} key={i}>
              <ImagePreview
                imageUrl={productDetails?.productImage}
                name={productDetails?.productName}
                width={64}
                height={64}
              />
              <div className="w-full">
                <div className="w-full flex flex-between">
                  <div className="text-sm font-medium text-default flex items-center gap-2">
                    <TruncatedText text={productDetails?.productName} maxLines={1} width={130} />
                    {!isMobile && (
                      <span className="text-sm font-normal">
                        {formatCurrency(Number(productDetails?.productPrice), defaultCurrency)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-start gap-2">
                    <CustomTooltip title="Edit Product">
                      <div
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer"
                        onClick={() => {
                          handleBack();
                        }}
                      >
                        <EditIcon color="var(--icon-default)" width={20} height={20} />
                      </div>
                    </CustomTooltip>
                    <CustomTooltip title="Remove Product">
                      <div
                        role="button"
                        tabIndex={0}
                        className="cursor-pointer"
                        onClick={() => {
                          removeProduct(product?.productId);
                        }}
                      >
                        <TrashIcon color="var(--icon-default)" width={20} height={20} />
                      </div>
                    </CustomTooltip>
                  </div>
                </div>
                {options?.length > 0 &&
                  options?.map((option: any, index: number) => (
                    <p key={index} className={styles.variantOption}>
                      <span className={styles.variantOptionHeading}>{option?.name}:</span>{' '}
                      <span className={styles.variantOptionValue}>{option?.value || 'N/A'}</span>
                    </p>
                  ))}
                {isMobile && (
                  <span className={`font-normal  ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    {formatCurrency(Number(productDetails?.productPrice), defaultCurrency)}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        <div className={styles.totalItemsContainer}>
          <div className="flex items-center gap-1">
            <div className={styles.totalItemsIcon}>
              <CartIcon color="var(--icon-default)" />
            </div>
            Total Items: <span className="text-default">{totalItems}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className={styles.totalItemsIcon}>
              <DollarIcon color="var(--icon-default)" />
            </div>
            Total Value: <span className="text-default">{formatCurrency(totalValue, defaultCurrency)}</span>
          </div>
        </div>
      </EnhancedModal>
    </div>
  );
}
